// hooks/useUIControls.ts - UI controls state management
import { useState, useEffect, useRef } from 'react';

export const useUIControls = () => {
  const [editingSegment, setEditingSegment] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [practiceMode, setPracticeMode] = useState(false);
  const [showMergedTimeline, setShowMergedTimeline] = useState(true);
  const [isDemo, setIsDemo] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const segmentNameInputRef = useRef<HTMLInputElement>(null);

  // Handle input focus when editing segment
  useEffect(() => {
    if (editingSegment && segmentNameInputRef.current) {
      segmentNameInputRef.current.focus();
    }
  }, [editingSegment]);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    
    if (!isFullscreen) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen()
          .then(() => setIsFullscreen(true))
          .catch(err => console.error('Error attempting to enable fullscreen:', err));
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
          .then(() => setIsFullscreen(false))
          .catch(err => console.error('Error attempting to exit fullscreen:', err));
      }
    }
  };

  // Toggle practice mode
  const togglePracticeMode = () => {
    setPracticeMode(prev => !prev);
  };

  // Toggle merged timeline visibility
  const toggleMergedTimeline = () => {
    setShowMergedTimeline(prev => !prev);
  };

  // Start editing segment
  const startEditingSegment = (segmentId: string) => {
    setEditingSegment(segmentId);
  };

  // Stop editing segment
  const stopEditingSegment = () => {
    setEditingSegment(null);
  };

  return {
    // State
    editingSegment,
    isFullscreen,
    practiceMode,
    showMergedTimeline,
    isDemo,
    
    // Refs
    containerRef,
    segmentNameInputRef,
    
    // Actions
    setIsDemo,
    toggleFullscreen,
    togglePracticeMode,
    toggleMergedTimeline,
    startEditingSegment,
    stopEditingSegment
  };
};
